import { useApi } from "@/hooks/useApi";
import { CalendarIntegrationStatusDTO } from "@shaarp/common";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/** The status DTO the API returns — tokens deliberately absent. */
type OutlookCalendarIntegration = CalendarIntegrationStatusDTO;

type OutlookCalendarIntegrationContextType = {
  outlookCalendarIntegration: OutlookCalendarIntegration | null;
  isLoading: boolean;
  refetch: () => Promise<void>;
};

const OutlookCalendarIntegrationContext =
  createContext<OutlookCalendarIntegrationContextType>({
    outlookCalendarIntegration: null,
    isLoading: true,
    refetch: async () => {},
  });

export const useOutlookCalendarIntegration =
  (): OutlookCalendarIntegrationContextType => {
    const context = useContext(OutlookCalendarIntegrationContext);
    if (!context) {
      throw new Error(
        "useOutlookCalendarIntegration must be used within a OutlookCalendarIntegrationProvider"
      );
    }
    return context;
  };

export const OutlookCalendarIntegrationProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const api = useApi();
  const [outlookCalendarIntegration, setOutlookCalendarIntegration] =
    useState<OutlookCalendarIntegration | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchOutlookCalendarIntegration = useCallback(async (): Promise<void> => {
    try {
      const { data } = await api.get<OutlookCalendarIntegration>(
        "/integration/calendar/outlook-calendar"
      );
      setOutlookCalendarIntegration(data);
    } catch (error) {
      setOutlookCalendarIntegration(null);
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  const refetch = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    await fetchOutlookCalendarIntegration();
  }, [fetchOutlookCalendarIntegration]);

  useEffect(() => {
    fetchOutlookCalendarIntegration();
  }, [fetchOutlookCalendarIntegration]);

  const value = useMemo(
    () => ({
      outlookCalendarIntegration,
      isLoading,
      refetch,
    }),
    [outlookCalendarIntegration, isLoading, refetch],
  );

  return (
    <OutlookCalendarIntegrationContext.Provider value={value}>
      {children}
    </OutlookCalendarIntegrationContext.Provider>
  );
};
